import {
  Component,
  OnInit,
  Input,
  AfterViewInit,
  AfterViewChecked,
  ViewChildren,
  ViewChild,
  QueryList,
  ElementRef,
  Host,
} from '@angular/core';
import { ProfileMin } from '../../../../profiles/shared/profile-min.model';
import { UtilService, DialogService } from '../../../../shared/index';
import { EventRead } from '../../../shared/event-read.model';
import { RegistrationUpsert } from '../../shared/crud/registration-upsert.model';
import { RegistrationAttendee } from '../../shared/registration-attendee.model';
import { RegistrationAttendeeItem } from '../../shared/registration-attendee-item.model';
import { RegistrationAttendeeItemCustomResponse } from '../../shared/registration-attendee-item-custom-response.model';
import { RegistrationItem } from '../../shared/registration-item.model';
import { RegistrationItemCustomField } from '../../shared/registration-item-custom-field.model';
import { RegistrationItemCustomFieldAvailableValue } from '../../shared/registration-item-custom-field-avail-value.model';
import { RegistrationItemCustomFieldAvailableValueCreditType } from '../../shared/registration-item-custom-field-avail-value-credit-type.model';
import { EventItemService } from '../../../shared/items/event-item.service';
import { EventItemSummary } from '../../../shared/items/event-item-summary.model';
import { IValidationComponent } from '../../../../shared/widgets/component/ivalidation.component';
import { ValidationResult } from '../../../../shared/widgets/validators/validation-result.model';
import { TranslationService } from '../../../../shared/translation/translation.service';
import { EventCustomRegFieldService } from '../../../shared/custom-reg-fields/event-custom-reg-field.service';
import { RegistrationService } from '../../shared/registration.service';
import { RegistrationCustomRegFieldResponse } from '../../shared/registration-custom-reg-field-response.model';
import { ProfileDupCheckerComponent } from '../../../../profiles/profile-dup-checker/profile-dup-checker.component';
import { RelatedProfileSelectorComponent } from '../../../../profiles/related-profile-selector/related-profile-selector.component';
import { EventCustomRegField } from '../../../shared/custom-reg-fields/event-custom-reg-field.model';
import { EventFieldVisibleRequiredEnum } from '../../../shared/event-field-visible-required.enum';
import { RegistrationWizardAttendeeComponent } from './attendee/registration-wizard-attendee.component';
import { ConfigSettingService } from '../../../../config/shared/config-setting/config-setting.service';

@Component({
  selector: 'wl-registration-wizard-attendees',
  templateUrl: 'registration-wizard-attendees.component.html',
  styleUrls: ['registration-wizard-attendees.component.css'],
  providers: [EventItemService, EventCustomRegFieldService],
  standalone: false
})
export class RegistrationWizardAttendeesComponent implements IValidationComponent {
  public registrationId: number;
  public registration: RegistrationUpsert = new RegistrationUpsert();
  @Input() public registrationProfile: ProfileMin = null;
  public event: EventRead = new EventRead();
  public initialLoadComplete: boolean = false;

  public _utilService: UtilService;
  public _registrationService: RegistrationService;
  public _eventItemService: EventItemService;
  public _translationService: TranslationService;
  public _eventCustomRegFieldService: EventCustomRegFieldService;
  public _eventFieldVisibleRequiredEnum: any = EventFieldVisibleRequiredEnum;
  public _iAmAsloAnAttendeeConfigSetting: boolean = true;

  public searchAllProfiles: boolean;
  public availableEventItems: Array<EventItemSummary>;
  public canAddNewAttendee: boolean;

  public allPossibleCustomFields: Array<EventCustomRegField>;

  public _loading: boolean = false;
  @ViewChildren(RegistrationWizardAttendeeComponent) public _attendeeComponents: QueryList<RegistrationWizardAttendeeComponent>;

  public constructor(
    utilService: UtilService,
    eventItemService: EventItemService,
    translationService: TranslationService,
    registrationService: RegistrationService,
    eventCustomRegFieldService: EventCustomRegFieldService,
    public _el: ElementRef,
    public _configSettingService: ConfigSettingService
  ) {
    this._utilService = utilService;
    this._eventItemService = eventItemService;
    this._translationService = translationService;
    this._eventCustomRegFieldService = eventCustomRegFieldService;
    this._registrationService = registrationService;

    this.searchAllProfiles = false;
    this.availableEventItems = new Array<EventItemSummary>();
    this.canAddNewAttendee = false;

    this.allPossibleCustomFields = new Array<EventCustomRegField>();
  }

  public loadData = (): Promise<boolean | void> => {
    let promises: Array<Promise<any>> = [];

    this._loading = true;

    promises.push(
      this._eventCustomRegFieldService.getEventCustomRegFields(this.event.EventId).then((data: any) => {
        this.allPossibleCustomFields = data.Result;
      })
    );

    let editAttendees: Array<RegistrationAttendee> = null;

    if (this.registrationId > 0) {
      promises.push(
        this._registrationService.getRegistrationAttendeesMin(this.registrationId).then((data: Array<RegistrationAttendee>) => {
          editAttendees = data;
        })
      );
    }

    promises.push(
      this._configSettingService.getConfigSetting('', 'EventRegistration', 'IAmAlsoAnAttendee').then((setting: any) => {
        if (setting && setting.SettingValue != null && (!setting.SettingValue || setting.SettingValue.toLowerCase() == 'false'))
          this._iAmAsloAnAttendeeConfigSetting = false;
      })
    );

    return Promise.all(promises).then(
      () => {
        if (this.registrationId > 0) {
          if (editAttendees && this.allPossibleCustomFields) {
            editAttendees.forEach((att: RegistrationAttendee) => {
              if (att.CustomFieldResponses)
                att.CustomFieldResponses.forEach(attCustomFieldResponse => {
                  if (!attCustomFieldResponse.CustomField) {
                    let customField = this.allPossibleCustomFields.find(x => x.Id == attCustomFieldResponse.EventCustomRegFieldId);

                    if (customField) attCustomFieldResponse.CustomField = customField;
                  }
                });
            });
          }

          this.registration.Attendees = editAttendees ? editAttendees : [];

          this.registration.Attendees.forEach((att: RegistrationAttendee, i: number) => {
            if (att.Items) att.Items.forEach(attendeeItem => (attendeeItem.Selected = true));
            this.addAllowedRegistrationAttendeeItemsToAttendee(att, false);
          });

          this._registrationService.calculateTotals(this.registration);
        }

        this.initialLoadComplete = true;
        this._loading = false;
      },
      () => (this._loading = false)
    );
  };

  public getValidationResult = (): ValidationResult => {
    let valid = new ValidationResult();
    valid.isValid = true;

    let selectedAttendees = this.registration.Attendees.filter((attendee: RegistrationAttendee) => {
      return (
        this._utilService.isNotNullEmptyOrWhitespace(attendee.Email) ||
        this._utilService.isNotNullEmptyOrWhitespace(attendee.FName) ||
        this._utilService.isNotNullEmptyOrWhitespace(attendee.LName)
      );
    });

    let allAttendeesHaveASelectedItem = !this.registration.Attendees.some(
      attendee =>
        !attendee.Items.some((attendeeItem: RegistrationAttendeeItem) => attendeeItem.Selected && attendeeItem.AttendeesForItem > 0)
    );

    if (allAttendeesHaveASelectedItem == false) {
      valid.isValid = false;
      valid.errors.push(
        this._translationService.getInstant('events.registration.wizard.attendees.attendees-not-assigned-to-item-validation-error')
      );
    }

    if (this._attendeeComponents) {
      let invalidAttendeesValidation = this._attendeeComponents.map(x => x.getValidationResult()).filter(x => !x.isValid);

      if (invalidAttendeesValidation.length) {
        valid.isValid = false;

        [].concat.apply([], invalidAttendeesValidation.map(x => x.errors)).forEach((e: string) => {
          if (valid.errors.indexOf(e) < 0) valid.errors.push(e);
        });
      }
    }

    return valid;
  };

  public updateAvailableAttendeeRegistrationItems = (autoAddOrRemoveDefaultAttendees: boolean): void => {
    this.updateCanAddNewAttendee();

    if (this.canAddNewAttendee && autoAddOrRemoveDefaultAttendees) {
      for (let attendee of this.registration.Attendees) this.addAllowedRegistrationAttendeeItemsToAttendee(attendee, false);

      let unassignedAttendees = this.registration.Attendees.filter(att => !att.Items.some(item => item.Selected));

      let flagTriggerAttendeeCustomFieldRefresh = false;

      if (unassignedAttendees.length) {
        let availableEventItems = this.registration.Items.filter(
          item => item.AttendeesForItem > 0 && this.getAttendeesRemainigForRegistrationItem(item.EventItemId) > 0
        );

        while (unassignedAttendees.length && availableEventItems.length) {
          let itemGonnaAssign = availableEventItems[0];
          let attendeeGonnaGetIt = unassignedAttendees[0];

          let attendeeItem = attendeeGonnaGetIt.Items.find(attItem => attItem.EventItemId == itemGonnaAssign.EventItemId);

          if (attendeeItem) {
            attendeeItem.Selected = true;
            flagTriggerAttendeeCustomFieldRefresh = true;
          } else {
            break;
          }

          unassignedAttendees = this.registration.Attendees.filter(att => !att.Items.some(item => item.Selected));
          availableEventItems = this.registration.Items.filter(
            item => item.AttendeesForItem > 0 && this.getAttendeesRemainigForRegistrationItem(item.EventItemId) > 0
          );
        }
      }

      this.updateCanAddNewAttendee();

      if (flagTriggerAttendeeCustomFieldRefresh) this._attendeeComponents.forEach(x => x.refreshCustomFields());

      while (this.canAddNewAttendee) {
        this.newAttendee();
      }

      for (let attendee of this.registration.Attendees) this.addAllowedRegistrationAttendeeItemsToAttendee(attendee, true);

      this.updateCanAddNewAttendee();
    } else if (autoAddOrRemoveDefaultAttendees && this.registration.Attendees && this.registration.Attendees.length) {
      let emptyEventItems = this.registration.Items.filter(item => item.AttendeesForItem > 0 && (!item.Quantity || item.Quantity <= 0));

      if (emptyEventItems.length) {
        let emptyEventItemIds = emptyEventItems.map(x => x.EventItemId);
        for (let attendee of this.registration.Attendees.filter(att => att.Items.some(x => emptyEventItemIds.indexOf(x.EventItemId) > -1)))
          attendee.Items = attendee.Items.filter(x => emptyEventItemIds.indexOf(x.EventItemId) < 0);
      }

      const getOverBookedEventItems = (items: Array<RegistrationItem>) => {
        return items
          .filter(item => item.AttendeesForItem > 0)
          .filter(item => {
            let attendeesForSelectedRegistrationItem = this.getAttendeesForRegistrationItem(item.EventItemId);
            return attendeesForSelectedRegistrationItem.length > 0 && this.getAttendeesRemainigForRegistrationItem(item.EventItemId) < 0;
          });
      };

      let overBookedEventItems: Array<RegistrationItem> = getOverBookedEventItems(this.registration.Items);

      while (overBookedEventItems.length) {
        overBookedEventItems.forEach(overBookedEventItem => {
          let attendeeEventItems = this.registration.Attendees.map(a =>
            a.Items.filter(otherItem => otherItem.EventItemId == overBookedEventItem.EventItemId && otherItem.Selected)
          ).filter(items => items.length > 0);

          if (attendeeEventItems.length) attendeeEventItems[attendeeEventItems.length - 1][0].Selected = false;
        });

        overBookedEventItems = getOverBookedEventItems(this.registration.Items);
      }

      this.registration.Attendees.filter(att => {
        return (!att.Items || !att.Items.some(x => x.Selected)) && !att.FName && !att.LName;
      }).forEach(att => {
        let i: number = this.registration.Attendees.indexOf(att);
        if (i >= 0) this.registration.Attendees.splice(i, 1);
      });

      this.updateCanAddNewAttendee();
    }
  };

  public getRegistrationItem = (eventItemId: number): RegistrationItem => {
    let registrationItems = this.registration.Items.filter((regItem: RegistrationItem) => {
      return regItem.EventItemId == eventItemId;
    });

    if (registrationItems.length > 0) {
      return registrationItems[0];
    } else {
      return new RegistrationItem();
    }
  };

  public getAttendeesForRegistrationItem = (eventItemId: number): Array<RegistrationAttendee> => {
    let attendeesForRegItem = new Array<RegistrationAttendee>();

    for (let attendee of this.registration.Attendees) {
      for (let item of attendee.Items) {
        if (item.Selected == true && item.EventItemId == eventItemId) {
          attendeesForRegItem.push(attendee);
        }
      }
    }

    return attendeesForRegItem;
  };

  public getAttendeesRemainigForRegistrationItem = (eventItemId: number): number => {
    let attendeesAllowedToAdd = 0;

    let registrationItem = this.getRegistrationItem(eventItemId);
    if (registrationItem.Quantity > 0) {
      let attendeesForSelectedRegistrationItem = this.getAttendeesForRegistrationItem(eventItemId);

      let currentAttendeeCountForRegistrationItem = attendeesForSelectedRegistrationItem.length;
      attendeesAllowedToAdd = registrationItem.AttendeesForItem * registrationItem.Quantity - currentAttendeeCountForRegistrationItem;
    }

    return attendeesAllowedToAdd;
  };

  public addAllowedRegistrationAttendeeItemsToAttendee = (attendee: RegistrationAttendee, preselectIfAvailable: boolean): void => {
    let itemListChanged: boolean = false;

    for (let registrationItem of this.registration.Items.filter(x => x.AttendeesForItem > 0 && x.Quantity > 0)) {
      let foundAttendeeEventItem = attendee.Items.filter(item => item.EventItemId == registrationItem.EventItemId);

      if (!foundAttendeeEventItem.length) {
        let regsitrationAttendeeItem = new RegistrationAttendeeItem();
        this._utilService.map(registrationItem, regsitrationAttendeeItem);

        regsitrationAttendeeItem.Remaining = this.getAttendeesRemainigForRegistrationItem(registrationItem.EventItemId);

        if (preselectIfAvailable) regsitrationAttendeeItem.Selected = regsitrationAttendeeItem.Remaining > 0;

        attendee.Items.push(regsitrationAttendeeItem);
        itemListChanged = true;
      } else {
        if (foundAttendeeEventItem[0]) {
          foundAttendeeEventItem[0].DisplayOrder = registrationItem.DisplayOrder;
          foundAttendeeEventItem[0].AttendeesForItem = registrationItem.AttendeesForItem;
        }
      }
    }

    if (itemListChanged) {
      attendee.Items.sort((a, b) => {
        return a.DisplayOrder < b.DisplayOrder
          ? -1
          : a.DisplayOrder > b.DisplayOrder
          ? 1
          : a.Descr.toLocaleLowerCase() < b.Descr.toLocaleLowerCase()
          ? -1
          : a.Descr.toLocaleLowerCase() > b.Descr.toLocaleLowerCase()
          ? 1
          : 0;
      });
    }
  };

  public getPotentialAttendeesForRegistrationItem = (eventItemId: number): number => {
    let attendeesAllowedToAdd = 0;

    let registrationItem = this.getRegistrationItem(eventItemId);

    attendeesAllowedToAdd = registrationItem.AttendeesForItem;
    if (registrationItem.Quantity > 0) {
      attendeesAllowedToAdd = registrationItem.AttendeesForItem * registrationItem.Quantity;
      let attendeesForSelectedRegistrationItem = this.getAttendeesForRegistrationItem(eventItemId);

      attendeesAllowedToAdd - attendeesForSelectedRegistrationItem.length;
    }

    if (attendeesAllowedToAdd < 0) {
      attendeesAllowedToAdd = 0;
    }
    return attendeesAllowedToAdd;
  };

  public attendeeItemSelectionChanged = (item: RegistrationAttendeeItem, attendee: RegistrationAttendee): void => {
    if (item.Selected) {
      let remainingAttendeesAvailable = this.getAttendeesRemainigForRegistrationItem(item.EventItemId);

      if (remainingAttendeesAvailable < 0) {
        let otherAttendeeItems = this.registration.Attendees.filter(a => a != attendee)
          .map(a => a.Items.filter(otherItem => otherItem.EventItemId == item.EventItemId && otherItem.Selected))
          .filter(items => items.length > 0);

        if (otherAttendeeItems.length) otherAttendeeItems[0][0].Selected = false;
      }
    }

    this.updateCanAddNewAttendee();
  };

  public newAttendee = (): void => {
    let newAttendee = new RegistrationAttendee();

    newAttendee.OrgName = this.registration.Registration.OrgName;
    newAttendee.Email = this.registration.Registration.Email;
    newAttendee.ProfileId = 0;
    if (this.registration.Attendees.length <= 0 && this._iAmAsloAnAttendeeConfigSetting) {
      this._utilService.mapSafe(this.registration.Registration, newAttendee);

      let fullName: string = newAttendee.LName;

      if (newAttendee.FName && newAttendee.FName.trim() != '') fullName = newAttendee.FName + ' ' + newAttendee.LName;

      newAttendee.NameForBadge = fullName;

      if (!this.registration.Registration.profileOrgInd && this.registration.Registration.ProfileId > 0)
        newAttendee.ProfileId = this.registration.Registration.ProfileId;
      else if (this.registration.Registration.relatedProfileId > 0) newAttendee.ProfileId = this.registration.Registration.relatedProfileId;
      else newAttendee.ProfileId = 0;
    }

    this.addAllowedRegistrationAttendeeItemsToAttendee(newAttendee, true);

    this.registration.Attendees.push(newAttendee);

    this.updateCanAddNewAttendee();
  };

  public removeAttendee = (attendee: RegistrationAttendee): void => {
    this._utilService.arrayRemoveEntry(this.registration.Attendees, attendee);
    this.updateCanAddNewAttendee();
  };

  public updateCanAddNewAttendee = (): void => {
    let newCanAddNewAttendee = false;
    for (let registrationItem of this.registration.Items) {
      if (registrationItem.Quantity > 0) {
        let attendeesAllowedForRegItem = this.getAttendeesRemainigForRegistrationItem(registrationItem.EventItemId);
        if (attendeesAllowedForRegItem > 0) {
          newCanAddNewAttendee = true;
        }
      }
    }

    if (this.canAddNewAttendee != newCanAddNewAttendee) this.canAddNewAttendee = newCanAddNewAttendee;
  };

  public onResponseChange = (response: any, attendee: RegistrationAttendee): void => {
    this._registrationService.calculateTotals(this.registration);
  };

  public setWillBeLoadingNext() {
    this._loading = true;
  }

  public addNewAttendee() {
    if (this._loading) return;

    this.newAttendee();
    setTimeout(() => this.scrollToAttendee(this.registration.Attendees.length - 1));
  }

  public scrollToAttendee(attendeeIndex: number) {
    this.scrollToElement('.wc-event-reg-wiz-attendee-' + attendeeIndex);
  }

  public scrollToElement(selector: string) {
    const target = this._el.nativeElement.querySelector(selector);
    const scrollParent = this._el.nativeElement.closest('.wl-tab-content-container');

    if (target && scrollParent) {
      const parentRect = scrollParent.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();

      const relativeTop = targetRect.top - parentRect.top;
      const targetScrollTop = scrollParent.scrollTop + relativeTop - 20;

      scrollParent.scrollTo({
        top: targetScrollTop,
        behavior: 'smooth'
      });
    }
  }

  public displayField(value: number): boolean {
    if ((value & 2) > 0) {
      return true;
    } else {
      return false;
    }
  }
}